import { WorkflowTrigger, TriggerType, TriggerContext } from './workflow-trigger-manager';
import { TaskStatus } from '../../types';
import path from 'path';

/**
 * 内置的智能工作流触发器
 */
export const builtinTriggers: WorkflowTrigger[] = [
  {
    id: 'prd-to-design',
    name: 'PRD 审批后生成设计文档',
    type: TriggerType.DOCUMENT_APPROVAL,
    enabled: true,
    workflowId: 'generate-design-doc',
    conditions: [
      {
        type: 'function',
        handler: async (context: TriggerContext) => {
          // 检查是否是 PRD 文档审批
          return context.metadata?.documentType === 'prd' &&
                 context.metadata?.approved === true;
        }
      }
    ],
    variables: {
      autoCreateTasks: true,
      templateType: 'full'
    },
    priority: 10
  },
  
  {
    id: 'design-to-tasks',
    name: '设计文档审批后创建任务',
    type: TriggerType.DOCUMENT_APPROVAL,
    enabled: true,
    workflowId: 'create-tasks-from-design',
    conditions: [
      {
        type: 'function',
        handler: async (context: TriggerContext) => {
          // 检查是否是设计文档审批
          return context.metadata?.documentType === 'design' &&
                 context.metadata?.approved === true;
        }
      }
    ],
    variables: {
      autoAssign: true,
      createTests: true
    },
    priority: 10
  },
  
  {
    id: 'ears-to-tasks',
    name: 'EARS 需求自动创建任务',
    type: TriggerType.EARS_REQUIREMENT_ADDED,
    enabled: true,
    workflowId: 'convert-ears-to-tasks',
    conditions: [
      {
        type: 'function',
        handler: async (context: TriggerContext) => {
          // 只处理高优先级需求
          const requirement = context.metadata?.requirement;
          return requirement?.priority === 'must' || 
                 requirement?.priority === 'should';
        }
      }
    ],
    variables: {
      includeTests: true,
      includeDocumentation: true
    },
    priority: 8,
    debounce: 3000 // 3秒防抖，避免批量添加时频繁触发
  },
  
  {
    id: 'task-completion-check',
    name: '任务完成时检查依赖',
    type: TriggerType.TASK_STATUS_CHANGE,
    enabled: true,
    workflowId: 'check-task-dependencies',
    conditions: [
      {
        type: 'function',
        handler: async (context: TriggerContext) => {
          // 任务从任意状态变为完成
          return context.metadata?.newStatus === 'done' ||
                 context.metadata?.newStatus === 'completed';
        }
      }
    ],
    variables: {
      notifyDependents: true,
      autoStartNext: true
    },
    priority: 9
  },
  
  {
    id: 'prd-file-update',
    name: 'PRD 文件更新时更新任务',
    type: TriggerType.FILE_CHANGE,
    enabled: true,
    workflowId: 'sync-prd-changes',
    conditions: [
      {
        type: 'pattern',
        pattern: '.workflow/docs/prd/*.md'
      }
    ],
    variables: {
      analyzeChanges: true,
      updateRelatedTasks: true
    },
    priority: 7,
    debounce: 5000, // 5秒防抖
    cooldown: 60000 // 1分钟冷却时间
  },
  
  {
    id: 'test-failure-handler',
    name: '测试失败时创建修复任务',
    type: TriggerType.FILE_CHANGE,
    enabled: false, // 默认禁用，需要用户手动启用
    workflowId: 'handle-test-failure',
    conditions: [
      {
        type: 'pattern',
        pattern: '**/test-results.json'
      },
      {
        type: 'function',
        handler: async (context: TriggerContext) => {
          // 检查测试是否失败
          if (!context.filePath) return false;
          try {
            const fs = await import('fs-extra');
            const results = await fs.readJson(context.filePath);
            return results.failed > 0;
          } catch {
            return false;
          }
        }
      }
    ],
    variables: {
      createBugTask: true,
      assignToAuthor: true
    },
    priority: 6,
    cooldown: 300000 // 5分钟冷却时间，避免重复创建
  },
  
  {
    id: 'code-review-trigger',
    name: '代码提交时触发审查流程',
    type: TriggerType.FILE_CHANGE,
    enabled: false, // 默认禁用
    workflowId: 'code-review-workflow',
    conditions: [
      {
        type: 'pattern',
        pattern: 'src/**/*.{ts,js,tsx,jsx}'
      }
    ],
    variables: {
      runLinter: true,
      runTests: true,
      generateReport: true
    },
    priority: 5,
    debounce: 10000 // 10秒防抖
  },
  
  {
    id: 'doc-sync',
    name: '文档变更时同步到知识库',
    type: TriggerType.FILE_CHANGE,
    enabled: true,
    workflowId: 'sync-documentation',
    conditions: [
      {
        type: 'pattern',
        pattern: '**/*.{md,mdx}'
      },
      {
        type: 'function',
        handler: async (context: TriggerContext) => {
          // 排除临时文件和草稿
          const filePath = context.filePath || '';
          return !filePath.includes('draft') && 
                 !filePath.includes('temp') &&
                 !filePath.includes('.workflow/');
        }
      }
    ],
    variables: {
      updateIndex: true,
      generateTags: true
    },
    priority: 4,
    debounce: 15000 // 15秒防抖
  },
  
  {
    id: 'milestone-completion',
    name: '里程碑完成时生成报告',
    type: TriggerType.TASK_STATUS_CHANGE,
    enabled: true,
    workflowId: 'generate-milestone-report',
    conditions: [
      {
        type: 'function',
        handler: async (context: TriggerContext) => {
          // 检查是否是里程碑任务
          const taskId = context.taskId;
          if (!taskId) return false;
          
          // TODO: 从 TaskStore 获取任务详情
          // 这里简化处理，假设任务ID包含 'milestone' 就是里程碑
          return taskId.includes('milestone') && 
                 context.metadata?.newStatus === 'done';
        }
      }
    ],
    variables: {
      includeMetrics: true,
      notifyTeam: true
    },
    priority: 10
  },
  
  {
    id: 'daily-standup',
    name: '每日站会提醒',
    type: TriggerType.SCHEDULE,
    enabled: false, // 需要用户配置时间后启用
    workflowId: 'daily-standup-reminder',
    conditions: [
      {
        type: 'expression',
        expression: 'cron(0 9 * * 1-5)' // 工作日早上9点
      }
    ],
    variables: {
      collectProgress: true,
      generateSummary: true
    },
    priority: 5
  }
];

/**
 * 获取内置触发器
 */
export function getBuiltinTriggers(): WorkflowTrigger[] {
  return builtinTriggers;
}

/**
 * 根据类型获取触发器
 */
export function getTriggersByType(type: TriggerType): WorkflowTrigger[] {
  return builtinTriggers.filter(t => t.type === type);
}

/**
 * 获取启用的触发器
 */
export function getEnabledTriggers(): WorkflowTrigger[] {
  return builtinTriggers.filter(t => t.enabled);
}